/**
 * Pernod Ricard Kazakhstan · Marketing / Digital
 * Google Analytics 4 (GA4) & Google Tag Manager (GTM) integration helper
 * Brand: Jameson
 */

// Ru / Kz alphabet transliteration mapping for city & product slugs
const ruKzToEnMap = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo', ж: 'zh',
    з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o',
    п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts',
    ч: 'ch', ш: 'sh', щ: 'shch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
    ә: 'a', ғ: 'g', қ: 'k', ң: 'n', ө: 'o', ұ: 'u', ү: 'u', һ: 'h', і: 'i'
};

/**
 * Transliterates Cyrillic text to a clean snake_case slug (e.g., 'Алматы' -> 'almaty')
 * @param {string} str 
 * @returns {string}
 */
export function toSlug(str) {
    if (!str || typeof str !== 'string') return '';
    return str
        .toLowerCase()
        .trim()
        .split('')
        .map(char => (ruKzToEnMap[char] !== undefined ? ruKzToEnMap[char] : char))
        .join('')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

/**
 * Pushes event/data payload to window.dataLayer
 * @param {Object} data 
 */
export function pushDataLayer(data) {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
    if (process.env.NODE_ENV !== 'production') {
        console.log('[Analytics dataLayer push]:', data);
    }
}

/**
 * Universal event dispatcher:
 * 1. Pushes { event: eventName, ...params } to window.dataLayer (for GTM/inspection)
 * 2. Calls window.gtag('event', eventName, params) (for GA4 direct hits via gtag.js)
 * @param {string} eventName 
 * @param {Object} [params] 
 */
export function trackEvent(eventName, params = {}) {
    pushDataLayer({
        event: eventName,
        ...params
    });
}

// Global active landing metadata
let currentLandingMeta = {
    brand: 'jameson',
    activation: 'jameson_merch_2026',
    landing_id: 'jameson-merch-2026',
    landing_type: 'registration'
};

/**
 * Задача 2: Идентификаторы активации (метаданные страницы) + отправка page_view
 * @param {Object} params
 * @param {string} params.brand - e.g., 'jameson'
 * @param {string} params.activation - e.g., 'jameson_merch_2026'
 * @param {string} params.landing_id - e.g., 'jameson-merch-2026'
 * @param {string} params.landing_type - e.g., 'registration'
 */
export function initLanding({ brand, activation, landing_id, landing_type }) {
    currentLandingMeta = {
        brand: brand || 'jameson',
        activation: activation || 'jameson_merch_2026',
        landing_id: landing_id || 'jameson-merch-2026',
        landing_type: landing_type || 'registration'
    };

    // 1. Push landing identifiers to dataLayer
    pushDataLayer(currentLandingMeta);

    // 2. Отправка события page_view при загрузке страницы с метаданными
    trackPageView();
}

/**
 * Отправляет событие page_view в GA4 и dataLayer
 * @param {Object} [customParams]
 */
export function trackPageView(customParams = {}) {
    const pageParams = {
        page_title: typeof document !== 'undefined' ? document.title : '',
        page_location: typeof window !== 'undefined' ? window.location.href : '',
        page_path: typeof window !== 'undefined' ? window.location.pathname : '',
        ...currentLandingMeta,
        ...customParams
    };

    trackEvent('page_view', pageParams);
}

// Track whether form_start has already been triggered on current page view
let formStarted = false;

/**
 * Сброс состояния form_start (при смене формы или маршрута)
 */
export function resetFormStart() {
    formStarted = false;
}

/**
 * Первый фокус / ввод в форму (один раз за сессию/загрузку)
 * @param {string|Object} [params] - formId или { formId, formMode }
 * @param {string} [formMode] - 'классика' | 'диалог'
 */
export function trackFormStart(params = 'main_registration', formMode = 'классика') {
    if (formStarted) return;
    formStarted = true;
    let resolvedFormId = 'main_registration';
    let resolvedFormMode = formMode || 'классика';

    if (typeof params === 'object' && params !== null) {
        resolvedFormId = params.formId || params.form_id || 'main_registration';
        resolvedFormMode = params.formMode || params.form_mode || resolvedFormMode;
    } else if (typeof params === 'string') {
        resolvedFormId = params;
    }

    trackEvent('form_start', {
        form_id: resolvedFormId,
        form_mode: resolvedFormMode
    });
}

/**
 * Задача 3: Нажатие кнопки отправки формы
 * @param {string} formId 
 */
export function trackFormSubmit(formId = 'main_registration') {
    trackEvent('form_submit', {
        form_id: formId
    });
}

/**
 * Задача 3: Сообщение об ошибке валидации или сервера
 * Внимание: НИКОГДА не передает персональные данные пользователя!
 * @param {Object} params
 * @param {string} params.formId
 * @param {'validation'|'server'|'captcha'} params.errorType
 * @param {string} params.fieldName - e.g., 'phone', 'email', 'name', 'city'
 * @param {string} params.errorTextCode - e.g., 'phone_invalid_format', 'field_required'
 */
export function trackFormError({
    formId = 'main_registration',
    errorType = 'validation',
    fieldName,
    errorTextCode
}) {
    trackEvent('form_error', {
        form_id: formId,
        error_type: errorType,
        field_name: fieldName,
        error_text_code: errorTextCode
    });
}

/**
 * Задача 3: Успешная регистрация (только после подтверждения сервера/OTP)
 * @param {Object} params
 * @param {string} params.formId
 * @param {string} [params.city] - Человекочитаемое или slug название города
 * @param {string} [params.productChoice] - Название выбранного продукта/релиза
 */
export function trackRegistrationSuccess({
    formId = 'main_registration',
    city = '',
    productChoice = ''
}) {
    const payload = {
        form_id: formId
    };

    if (city) {
        payload.city = toSlug(city);
    }
    if (productChoice) {
        payload.product_choice = toSlug(productChoice);
    }

    trackEvent('registration_success', payload);
}

/**
 * Задача 3: Проверка возраста
 * @param {boolean} passed 
 */
export function trackAgeGate(passed) {
    trackEvent(passed ? 'age_gate_pass' : 'age_gate_fail', {
        user_choice: passed ? 'yes' : 'no',
        brand: 'jameson',
        landing_id: 'jameson-triple-trip'
    });
}

/**
 * Трекинг кликов по CTA кнопкам (переход к анкете или блоку)
 * @param {Object} params
 * @param {'hero'|'how'|string} params.location
 * @param {string} [params.ctaName]
 * @param {string} [params.brand]
 * @param {string} [params.lang]
 */
export function trackCtaClick({ location = 'hero', ctaName = 'fill_form', brand = 'jameson', lang = 'ru' }) {
    trackEvent('cta_click', {
        location,
        cta_name: ctaName,
        brand,
        lang
    });
}

/**
 * Трекинг прохождения отдельных шагов / полей формы
 * @param {Object} params
 * @param {string} params.stepName - e.g., 'first_name', 'phone', 'city', 'otp_verified'
 * @param {string} [params.formId]
 * @param {string} [params.brand]
 */
export function trackFormStepComplete({ stepName, formId = 'teaser_registration', brand = 'jameson' }) {
    trackEvent('form_step_complete', {
        step_name: stepName,
        form_id: formId,
        brand
    });
}

/**
 * Трекинг успешного лида с указанием региона
 * @param {Object} params
 * @param {string} params.region - 'Алматы' | 'Астана' | 'Север' | 'Восток' | 'Запад' | 'Юг'
 * @param {string} [params.city]
 * @param {string} [params.brand]
 * @param {string} [params.lang]
 */
export function trackGenerateLead({ region, city = '', brand = 'jameson', lang = 'ru' }) {
    trackEvent('generate_lead', {
        region: region || 'Не указан',
        city: toSlug(city),
        brand,
        lang
    });
}

/**
 * Трекинг технических сбоев и ошибок при отправке формы
 * @param {Object} params
 * @param {string} params.errorType
 * @param {string} [params.errorMessage]
 * @param {string} [params.step]
 * @param {string} [params.brand]
 */
export function trackFormSubmitError({ errorType = 'technical', errorMessage = '', step = '', brand = 'jameson' }) {
    trackEvent('form_submit_error', {
        error_type: errorType,
        error_message: errorMessage ? String(errorMessage).slice(0, 150) : 'Technical error',
        step,
        brand
    });
}

/**
 * Трекинг JavaScript ошибок
 * @param {Object} params
 * @param {string} params.errorMessage
 * @param {string} [params.errorSource]
 * @param {number} [params.errorLine]
 * @param {number} [params.errorCol]
 * @param {string} [params.brand]
 */
export function trackJsError({ errorMessage, errorSource = '', errorLine = 0, errorCol = 0, brand = 'jameson' }) {
    trackEvent('js_error', {
        error_message: errorMessage ? String(errorMessage).slice(0, 200) : 'Unknown JS Error',
        error_source: errorSource,
        error_line: errorLine,
        error_col: errorCol,
        brand
    });
}

/**
 * Задача 4: Виртуальный просмотр страницы в SPA (экран «спасибо»)
 * @param {string} path - e.g., '/jameson/thank-you'
 * @param {string} title - e.g., 'Спасибо за регистрацию'
 */
export function trackVirtualPageView(
    path = '/jameson/thank-you',
    title = 'Спасибо за регистрацию'
) {
    trackEvent('virtual_page_view', {
        virtual_path: path,
        virtual_title: title,
        page_location: typeof window !== 'undefined' ? window.location.origin + path : path,
        page_path: path,
        page_title: title,
        ...currentLandingMeta
    });
}

/**
 * Обновляет состояние Google Consent Mode v2 без генерации события клика
 * @param {'granted'|'denied'} status
 */
export function updateConsentState(status = 'granted') {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
            analytics_storage: status,
            ad_storage: status,
            ad_user_data: status,
            ad_personalization: status
        });
    }
}

/**
 * Задача 5: Пользователь нажал «Принять» на плашке согласия на cookies
 * 1. Обновляет Consent Mode v2 (analytics_storage, ad_storage -> granted)
 * 2. Пушит событие cookie_consent (с consent_analytics: granted, consent_marketing: granted)
 * 3. Отправляет page_view для полного учета сессии
 * @param {boolean} granted 
 */
export function trackCookieConsent(granted = true) {
    const status = granted ? 'granted' : 'denied';

    // 1. Обновление Consent Mode v2 в gtag
    updateConsentState(status);

    // 2. Пуш события cookie_consent в dataLayer и gtag
    trackEvent('cookie_consent', {
        consent_analytics: status,
        consent_marketing: status
    });

    // Совместимость с consent_update для обратной совместимости
    pushDataLayer({
        event: 'consent_update',
        analytics_consent: status,
        ads_consent: status
    });

    // 3. Если согласие получено, гарантированно логируем page_view с куками
    if (granted) {
        trackPageView();
    }
}

// Экспорт алиаса для обратной совместимости
export const trackConsent = trackCookieConsent;
